import { useState, useEffect } from 'react';
import { X, Copy, Check, Code2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { storiesApi } from '../../api/client';
import { useEditorStore } from '../../store/editorStore';
import { patchAmpFormLayers } from '../../utils/ampFormPatcher';

export default function AmpCodePanel() {
  const story = useEditorStore((s) => s.story);
  const toggleAmpCodePanel = useEditorStore((s) => s.toggleAmpCodePanel);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (story) {
      generatePreview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id]);

  async function generatePreview() {
    if (!story) return;
    setLoading(true);
    try {
      const result = await storiesApi.export(story.id, { format: 'amp-html', minify: false });
      setHtml(patchAmpFormLayers(result.html || '', story));
    } catch {
      setHtml('<!-- Error generating AMP code -->');
    } finally {
      setLoading(false);
    }
  }

  async function copyToClipboard() {
    await navigator.clipboard.writeText(html);
    setCopied(true);
    toast.success('AMP code copied!');
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl
                    h-[70vh] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Code2 size={15} className="text-indigo-400" />
          <h3 className="text-sm font-semibold text-gray-200">AMP Code Preview</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generatePreview}
            disabled={loading}
            className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
          >
            {loading ? <Loader2 size={11} className="animate-spin" /> : '↻'}
            Refresh
          </button>
          <button onClick={copyToClipboard} className="icon-btn w-6 h-6" title="Copy code">
            {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
          </button>
          <button onClick={toggleAmpCodePanel} className="icon-btn w-6 h-6">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Info bar */}
      <div className="px-3 py-2 bg-indigo-900/20 border-b border-indigo-800/30">
        <p className="text-xs text-indigo-400">
          This is the generated AMP HTML. Copy it and host on a web server to publish.
        </p>
      </div>

      {/* Code */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 size={20} className="animate-spin text-indigo-500" />
          </div>
        ) : (
          <pre className="amp-code-viewer p-3 text-xs leading-relaxed whitespace-pre overflow-x-auto min-w-0 text-white">
            {html || '<!-- No content generated yet →'}
          </pre>
        )}
      </div>
    </div>
  );
}
